// The signed-in frame (mockup buildShell + renderRoute): skip link · left rail · header · tool bar · main · footer ·
// phone bottom tabs · search. Decides whether the person may open the route (menu rule + route perm), keeps the
// address in step with the view state, shows the one-time "주소의 조건 일부를 바꿔 열었어요" notice, moves focus to the
// new screen's h1 after navigation and closes floating things.
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useRouteError } from 'react-router';
import { usePerms } from '../auth/AuthContext';
import { canAny, menuItem, menuItemForPath } from '../auth/permissions';
import { useT } from '../i18n';
import { useIsMobile, useIsTablet } from '../lib/media';
import { cls, josa, store } from '../lib/util';
import { useViewStore } from '../state/store';
import { readUrlState, type UrlNote } from '../state/url';
import { closeFloating } from '../ui/layers';
import { Alert, Skeleton } from '../ui/States';
import { CommandPalette } from './CommandPalette';
import { Footer } from './Footer';
import { Header } from './Header';
import { BottomTabs, SideRail } from './Navigation';
import { ScopeBar } from './ScopeBar';
import { StateScreen } from './StateScreen';
import { useUrlStateSync } from './sync';
import { useTitleOverride } from './title';
import { ToolbarSlotContext, useToolbarSlot } from './Toolbar';
import { useRouteHandle, useView } from './view';

function focusMainHeading(tries = 0) {
  const h1 = document.querySelector<HTMLElement>('#main h1');
  if (!h1) {
    if (tries < 30) requestAnimationFrame(() => focusMainHeading(tries + 1));
    return;
  }
  if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
  h1.focus({ preventScroll: true });
}

export function AppShell() {
  const t = useT();
  const perms = usePerms();
  const location = useLocation();
  const handle = useRouteHandle();
  const view = useView();
  const tablet = useIsTablet();
  const mobile = useIsMobile();
  const [narrow, setNarrow] = useState(() => store.get('rail') === 'narrow');
  const [railOpen, setRailOpen] = useState(false);
  const [palette, setPalette] = useState(false);
  const [slot, setToolbarEl, hasToolbar] = useToolbarSlot();
  const override = useTitleOverride();
  const rawTeam = useViewStore((s) => s.team);
  const setTeam = useViewStore((s) => s.setTeam);
  const urlNotes = useViewStore((s) => s.urlNotes);
  const addUrlNotes = useViewStore((s) => s.addUrlNotes);
  const clearUrlNotes = useViewStore((s) => s.clearUrlNotes);
  const firstUrl = useRef(readUrlState(location.search));
  const notesPath = useRef(location.pathname);
  useUrlStateSync();

  const mi = handle?.menuId ? menuItem(handle.menuId) : menuItemForPath(location.pathname);
  const current = mi?.id ?? null;
  const allowed = (!mi || mi.show(perms)) && (!handle?.perm || canAny(perms, handle.perm));
  const title = override ?? (handle ? t(handle.titleKey) : t('state.notFound.page'));

  useEffect(() => {
    document.title = t('app.docTitle', { page: title });
  }, [title, t]);

  // link conditions the reader may not use: say so once (scope, team), then drop the unusable team.
  // The team list comes from GET /api/v1/teams, so the check waits until it is known.
  const notesDone = useRef(false);
  useEffect(() => {
    if (notesDone.current || !view.teamsReady) return;
    notesDone.current = true;
    const u = firstUrl.current;
    const notes: UrlNote[] = [];
    if (u.scope && u.scope !== view.scope) notes.push({ key: 'url.fix.scope', params: { want: u.scope, got: view.scope } });
    if (u.team && view.teamFilterOn && !view.team) notes.push({ key: view.teams.some((x) => x.id === u.team) ? 'url.fix.teamSmall' : 'url.fix.team' });
    addUrlNotes(notes);
    // only for the address the app was opened with
  }, [view.teamsReady]);
  useEffect(() => {
    if (rawTeam && !view.team) setTeam(null);
  }, [rawTeam, view.team, setTeam]);

  // navigation: close floating things; new screen → top of the page and focus on its h1
  const firstPath = useRef(true);
  useEffect(() => {
    closeFloating();
    if (firstPath.current) {
      firstPath.current = false;
      return;
    }
    if (location.pathname !== notesPath.current) clearUrlNotes();
    setRailOpen(false);
    window.scrollTo(0, 0);
    requestAnimationFrame(() => focusMainHeading());
  }, [location.pathname, clearUrlNotes]);
  useEffect(() => {
    closeFloating();
  }, [location.search]);
  useEffect(() => {
    setRailOpen(false);
  }, [tablet]);

  // Ctrl/⌘+K and "/" open search (not while typing or with a dialog open); Esc closes the tablet rail overlay
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof Element && !!e.target.closest('input, textarea, select, [contenteditable="true"]');
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setPalette(true);
        return;
      }
      if (e.key === '/' && !typing && !e.ctrlKey && !e.metaKey && !e.altKey && !document.querySelector('dialog[open]')) {
        e.preventDefault();
        setPalette(true);
        return;
      }
      if (e.key === 'Escape' && railOpen) {
        setRailOpen(false);
        document.querySelector<HTMLElement>('.side-rail__toggle')?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [railOpen]);

  const toggleRail = () => {
    if (tablet) {
      setRailOpen((v) => !v);
      return;
    }
    setNarrow((v) => {
      store.set('rail', v ? 'wide' : 'narrow');
      return !v;
    });
  };

  const noteText = (n: UrlNote): string => {
    if (n.key === 'url.fix.scope') {
      const want = t('scope.' + String(n.params?.want));
      const got = t('scope.' + String(n.params?.got));
      return t(n.key, { want, eun: josa(want, '은', '는'), got, ro: josa(got, '으로', '로') });
    }
    if (n.key === 'url.fix.period') {
      const got = t('period.long.7d');
      return t(n.key, { got, ro: josa(got, '으로', '로') });
    }
    return t(n.key, n.params);
  };
  const showNotes = urlNotes.length > 0 && location.pathname === notesPath.current;

  return (
    <>
      <a
        className="skip-link"
        href="#main"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('main')?.focus();
        }}
      >
        {t('app.skip')}
      </a>
      <div className={cls('app-shell', narrow && 'is-rail-narrow', railOpen && 'is-rail-open')}>
        <SideRail current={current} narrow={narrow} open={railOpen} tablet={tablet && !mobile} onToggle={toggleRail} onNavigate={() => setRailOpen(false)} />
        <div className="rail-scrim" aria-hidden="true" onClick={() => setRailOpen(false)} />
        <div className="app-col">
          <Header title={title} onOpenPalette={() => setPalette(true)} />
          <div className="filter-bar" hidden={!hasToolbar}>
            <div className="filter-bar__inner" ref={setToolbarEl} />
          </div>
          <ToolbarSlotContext.Provider value={slot}>
            <main id="main" className="app-main" tabIndex={-1}>
              <ScopeBar where="main" />
              {showNotes ? <Alert id="url-fix" tone="info" dismissible title={t('url.fix.title')} desc={urlNotes.map(noteText).join(' ')} /> : null}
              {allowed ? (
                <Suspense fallback={<Skeleton lines={5} />}>
                  <Outlet />
                </Suspense>
              ) : (
                <StateScreen kind="noAccess" />
              )}
            </main>
          </ToolbarSlotContext.Provider>
          <Footer />
        </div>
      </div>
      <BottomTabs current={current} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
    </>
  );
}

/** Route error element inside the shell: "화면을 그리지 못했어요" + [지금 다시 시도] (the shell stays usable). */
export function ScreenError() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    console.error('[aiax-hub] screen failed to render', error);
  }, [error]);
  return <StateScreen kind="error" onRetry={() => navigate(location.pathname + location.search, { replace: true })} />;
}
